import { useCallback, useEffect, useState } from "react";
import { api, apiError, fmtDateTime } from "../lib/api";
import { toast } from "sonner";
import PhotoAttach from "../components/PhotoAttach";
import { CheckCircle2, Package, Truck, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const STATUS_LABEL = {
    REQUESTED: "Requested", APPROVED: "Approved", ORDERED: "Ordered", DELIVERED: "Delivered", CANCELLED: "Cancelled",
};
const STATUS_COLOR = {
    REQUESTED: "bg-amber-50 text-amber-700 border-amber-200",
    APPROVED: "bg-indigo-50 text-indigo-700 border-indigo-200",
    ORDERED: "bg-blue-50 text-blue-700 border-blue-200",
    DELIVERED: "bg-emerald-50 text-emerald-700 border-emerald-200",
    CANCELLED: "bg-gray-50 text-gray-500 border-gray-200",
};

export default function PurchaseRequestsPage({ siteFilter }) {
    const { user } = useAuth();
    const isAdmin = user?.role === "HO_ADMIN";
    const [rows, setRows] = useState([]);
    const [statusFilter, setStatusFilter] = useState("");
    const [loading, setLoading] = useState(true);
    const [delivering, setDelivering] = useState(null); // pr row
    const [deliverQty, setDeliverQty] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (siteFilter) params.site_id = siteFilter;
            if (statusFilter) params.status = statusFilter;
            const { data } = await api.get("/purchase-requests", { params });
            setRows(data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, [siteFilter, statusFilter]);
    useEffect(() => { load(); }, [load]);

    async function transition(pr, status, extra = {}) {
        try { await api.patch(`/purchase-requests/${pr.id}`, { status, ...extra }); toast.success(`${STATUS_LABEL[status]}`); load(); }
        catch (e) { toast.error(apiError(e)); }
    }
    async function deliver() {
        if (!delivering || !deliverQty || Number(deliverQty) <= 0) { toast.error("Enter received quantity"); return; }
        try {
            await api.post(`/purchase-requests/${delivering.id}/deliver`, { qty_received: Number(deliverQty) });
            toast.success("Delivered — stock updated");
            setDelivering(null); setDeliverQty("");
            load();
        } catch (e) { toast.error(apiError(e)); }
    }

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-10">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Purchase Queue</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1">Purchase Requests</h1>
                </div>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm" data-testid="pr-status-filter">
                    <option value="">All statuses</option>
                    {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
            </div>
            <div className="mt-6 rounded-xl bg-white border border-gray-100 fx-shadow-card overflow-hidden">
                <table className="w-full text-sm" data-testid="pr-table">
                    <thead className="bg-[#F4F6F9]">
                        <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                            <th className="px-4 py-3">Site</th>
                            <th className="px-4 py-3">Item</th>
                            <th className="px-4 py-3 text-right">Qty</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3">Created</th>
                            <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {loading ? <tr><td colSpan="6" className="p-6 text-center text-sm text-gray-500">Loading…</td></tr> :
                        rows.length === 0 ? <tr><td colSpan="6" className="p-6 text-center text-sm text-gray-500" data-testid="pr-empty">No purchase requests.</td></tr> :
                        rows.map((r) => (
                            <tr key={r.id} data-testid={`pr-row-${r.id}`}>
                                <td className="px-4 py-3 text-xs text-gray-600 font-medium">{r.site_name}</td>
                                <td className="px-4 py-3 font-medium text-[#1E3355] inline-flex items-center gap-1.5"><Package className="w-3.5 h-3.5 text-gray-400" /> {r.item_name} <span className="text-[10px] text-gray-400">/{r.item_unit}</span></td>
                                <td className="px-4 py-3 text-right tabular-nums">{r.qty_received ?? r.qty_ordered ?? r.qty_suggested}</td>
                                <td className="px-4 py-3"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${STATUS_COLOR[r.status]}`}>{STATUS_LABEL[r.status]}</span></td>
                                <td className="px-4 py-3 text-[11px] text-gray-500">{fmtDateTime(r.created_at)}</td>
                                <td className="px-4 py-3 text-right">
                                    <div className="inline-flex items-center gap-1">
                                        {isAdmin && r.status === "REQUESTED" && <button onClick={() => transition(r, "APPROVED")} className="text-xs font-medium text-indigo-700 hover:bg-indigo-50 rounded px-2 py-1" data-testid={`pr-approve-${r.id}`}>Approve</button>}
                                        {isAdmin && (r.status === "REQUESTED" || r.status === "APPROVED") && <button onClick={() => transition(r, "ORDERED", { qty_ordered: r.qty_suggested })} className="text-xs font-medium text-blue-700 hover:bg-blue-50 rounded px-2 py-1" data-testid={`pr-order-${r.id}`}>Mark ordered</button>}
                                        {r.status === "ORDERED" && <button onClick={() => { setDelivering(r); setDeliverQty(String(r.qty_ordered ?? r.qty_suggested)); }} className="text-xs font-semibold text-emerald-700 hover:bg-emerald-50 rounded px-2 py-1 inline-flex items-center gap-1" data-testid={`pr-deliver-${r.id}`}><Truck className="w-3 h-3" /> Deliver</button>}
                                        {isAdmin && ["REQUESTED", "APPROVED", "ORDERED"].includes(r.status) && <button onClick={() => transition(r, "CANCELLED")} className="text-xs font-medium text-gray-500 hover:bg-gray-100 rounded px-2 py-1" data-testid={`pr-cancel-${r.id}`}>Cancel</button>}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {delivering && (
                <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="deliver-modal">
                    <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
                        <div className="p-5 border-b border-gray-100 flex items-start justify-between">
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Mark delivered</div>
                                <div className="font-display font-semibold text-[#1E3355] mt-0.5">{delivering.item_name} · {delivering.site_name}</div>
                            </div>
                            <button onClick={() => setDelivering(null)} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="p-5 space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Quantity received</label>
                                <input type="number" value={deliverQty} onChange={(e) => setDeliverQty(e.target.value)} min="0" step="0.01" className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" data-testid="deliver-qty" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Delivery photo <span className="text-[#E02B2B]">*</span></label>
                                <div className="text-[11px] text-gray-500 mb-1.5">At least one photo required before delivery is marked.</div>
                                <PhotoAttach
                                    parentType="stock_delivery"
                                    parentId={delivering.id}
                                    siteId={delivering.site_id}
                                    tag="delivery"
                                    testIdPrefix="deliver-photo"
                                />
                            </div>
                        </div>
                        <div className="flex border-t border-gray-100">
                            <button onClick={() => setDelivering(null)} className="flex-1 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50" data-testid="deliver-cancel">Cancel</button>
                            <button onClick={deliver} className="flex-1 py-3 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 inline-flex items-center justify-center gap-1.5" data-testid="deliver-submit"><CheckCircle2 className="w-4 h-4" /> Confirm delivery</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
